
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  ProfileMapInterface,
} from '@/services/QC-V2/chat/interfaces';
import { CreateTagInterface, TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';
import { ScoreTypeInterface } from '@/services/QC-V2/score-type/interfaces';
import { ScoreGradeInterface } from '@/services/QC-V2/score-grade/interfaces';
import FeedbackDialog, { FeedbackDialogDataInterface } from '@/components/dialogs/qc-v2/feedback/FeedbackDialog.vue';
import {
  FeedbackRepositoryInterface, FeedbackStatus, TicketFeedbackCreateInterface,
} from '@/services/QC-V2/feedback/interfaces';
import {
  EscalationRepositoryInterface,
  TicketEscalationCreateData,
} from '@/services/QC-V2/escalation/interfaces';
import TicketRepositoryInterface, {
  TicketReviewFormDataInterface,
  TicketInterface,
} from '@/services/QC-V2/ticket/interfaces';
import TicketComments from '@/components/qa/ticket/TicketComments.vue';
import TicketEscalateDialog from '@/components/dialogs/qc-v2/escalation/TicketEscalateDialog.vue';
import ChatComments from '@/components/qa/chat/ChatComments.vue';

interface ProfilesSelectInterface {
  id: string,
  name: string,
}

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    ChatComments,
    TicketComments,
    FeedbackDialog,
    TicketEscalateDialog,
  },
  inject: [
    'ticketRepositoryV2',
    'scoreTypeRepositoryV2',
    'scoreGradeRepositoryV2',
    'tagRepositoryV2',
    'feedbackRepositoryV2',
    'escalationRepositoryV2',
    'userRepository',
  ],
})
export default class TicketReview extends Vue {
  public $refs!: { 'review-comments': HTMLFormElement };
  @Prop() private ticket!: TicketInterface;
  @Prop() private scoreTypes!: ScoreTypeInterface[];
  @Prop() private scoreGrades!: ScoreGradeInterface[];
  @Prop() private tags!: TagInterface[];
  @Prop() private profiles!: ProfilesSelectInterface[];
  @Prop() private profilesMap!: ProfileMapInterface;

  private loading: boolean = true;

  private ticketRepositoryV2!: TicketRepositoryInterface;
  private tagRepositoryV2!: TagRepositoryInterface;

  private feedbackRepositoryV2!: FeedbackRepositoryInterface;
  private escalationRepositoryV2!: EscalationRepositoryInterface;

  private commentProfiles: ProfileMapInterface = {};

  private searchTag: string = '';
  private selectedTags: TagInterface[] = [];

  private formValid: boolean = false;

  private reviewSubmitted: boolean = false;

  private selectedUser: string = 'ticket';

  private formData: { [userId: string]: TicketReviewFormDataInterface } = {};

  private dataForFeedbackDialog: FeedbackDialogDataInterface | undefined = undefined;
  private feedbackFormValid: boolean = false;
  private feedbackDialogVisible: boolean = false;
  private draftValid: boolean = false;
  private escalateFormValid: boolean = false;
  private escalateDialogVisible: boolean = false;

  private async created(): Promise<void> {
    await this.initialize();
  }

  @Watch('ticket')
  private async initialize(): Promise<void> {
    this.loading = true;
    this.initializeFormData();
    this.extractReviewData();
    this.loading = false;
  }

  private initializeFormData(): void {
    this.searchTag = '';
    this.selectedTags = [];
    this.reviewSubmitted = false;
    this.selectedUser = 'ticket';
    this.formData = {
      ticket: {
        ticketId: this.ticket.id,
        reviewerId: this.$store.getters.id,
        isDraft: false,
        reviewedId: 'ticket',
        scores: {},
        tags: [],
        comments: [],
      },
    };

    this.profiles.forEach((profile) => {
      this.formData[profile.id] = {
        ticketId: this.ticket.id,
        reviewerId: this.$store.getters.id,
        isDraft: false,
        reviewedId: profile.id,
        scores: {},
        tags: [],
        comments: [],
      };
    });
  }

  private addScore(scoreTypeId: number, scoreGradeId: number): void {
    this.formData[this.selectedUser].scores[scoreTypeId] = scoreGradeId;
    this.validateForm();
  }

  private removeScore(scoreTypeId: number): void {
    delete this.formData[this.selectedUser].scores[scoreTypeId];
    this.validateForm();
  }

  private async review(): Promise<void> {
    if (this.reviewHasUnsentComment()) {
      this.$infoDialog.show('You have unsent comment!');
      return;
    }

    try {
      if (this.formData[this.selectedUser].reviewedId === 'ticket') {
        this.reviewSubmitted = true;
        await this.addTicketTags();
        this.reviewSubmitted = false;
        return;
      }
      const existingReviewedIds = this.ticket.reviews
        .filter((review) => !review.isDraft)
        .map((review) => review.reviewedId);

      const reviewData = Object.values(this.formData)
        .filter((data) => data.reviewedId !== 'ticket'
          && !existingReviewedIds.includes(data.reviewedId)
          && Object.values(data.scores).length);

      if (!reviewData.length) {
        this.$errorDialog.show('Invalid review data!');
        return;
      }

      await this.ticketRepositoryV2.reviewTicket(reviewData);
      this.addReviews(reviewData);

      this.reviewSubmitted = true;
      this.$successDialog.show('Review created successfully');
      this.$emit('ticket-reviewed', this.ticket);
      this.initializeFormData();
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private async saveAsDraft(): Promise<void> {
    this.formData[this.selectedUser].isDraft = true;
    await this.review();
  }

  private validateForm(): void {
    if (this.formData[this.selectedUser].reviewedId === 'ticket' && this.formData.ticket.tags.length) {
      this.formValid = true;
      this.feedbackFormValid = false;
      return;
    }

    const valid = (Object.values(this.formData[this.selectedUser].scores).every((score) => !!score))
      && (Object.keys(this.formData[this.selectedUser].scores).length === this.scoreTypes.length)
      && (!!this.formData[this.selectedUser].reviewedId);

    this.formValid = valid && ((!!this.formData[this.selectedUser].comments.length && !!this.formData[this.selectedUser].tags.length)
      || (!this.formData[this.selectedUser].comments.length && !this.formData[this.selectedUser].tags.length));

    this.feedbackFormValid = this.formValid
      && !this.ticket.feedbacks.find((feedback) => feedback.feedbackTo === this.selectedUser)
      && !!this.formData[this.selectedUser].tags.length;

    this.draftValid = valid && !this.ticket.reviews.find((review) => review.reviewedId === this.selectedUser);

    this.escalateFormValid = Object.values(this.formData)
        .filter((data) => data.reviewedId !== 'ticket')
        .every((data) => Object.values(data.scores).filter((score) => !!score).length === this.scoreTypes.length)
      || Object.values(this.formData)
        .every((data) => Object.values(data.scores).filter((score) => !!score).length === 0);

    this.getDataForFeedbackDialog();
  }

  private async addTicketTags(): Promise<void> {
    if (this.formData[this.selectedUser].reviewedId !== 'ticket') {
      return;
    }

    try {
      await this.tagRepositoryV2
        .addTicketTags(this.ticket.id, this.formData[this.selectedUser].tags.map((tag) => tag.id));
      this.$successDialog.show('Tags added to ticket successfully');
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private async createTag(): Promise<void> {
    if (!this.searchTag) {
      return;
    }

    const data = {
      label: this.searchTag,
      color: '#808080FF',
    } as CreateTagInterface;

    const newTag = await this.tagRepositoryV2.createTag(data);

    this.tags.push(newTag);
    this.formData[this.selectedUser].tags.push(newTag);
  }

  private async createFeedback(): Promise<void> {
    const feedbackData: TicketFeedbackCreateInterface = {
      isDraft: false,
      ticketId: this.ticket.id,
      feedbackTo: this.formData[this.selectedUser].reviewedId,
      feedbackFrom: this.$store.getters.id,
      status: FeedbackStatus.SENT,
    };

    try {
      this.formData[this.selectedUser].isDraft = false;
      await this.feedbackRepositoryV2.createTicketFeedback(feedbackData, this.formData[this.selectedUser]);
      this.addReviews(Object.values(this.formData));
      this.$emit('ticket-reviewed', this.ticket);
      this.$successDialog.show('Feedback created Successfully');
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private async createEscalation(data: TicketEscalationCreateData): Promise<void> {
    if (this.ticket.escalation) {
      this.$successDialog.show('Ticket already escalated');
      return;
    }
    try {
      let reviewsData: TicketReviewFormDataInterface[] = [];
      const hasReviews = Object.values(this.formData)
        .filter((reviewData) => reviewData.reviewedId !== 'ticket')
        .every((reviewData) => Object.values(reviewData.scores)
          .filter((score) => !!score).length === this.scoreTypes.length);

      if (hasReviews) {
        reviewsData = Object.values(this.formData)
          .filter((reviewData) => reviewData.reviewedId !== 'ticket'
            && !this.ticket.reviews.find((review) => !review.deletedAt && review.reviewedId === reviewData.reviewedId));
      }

      data.agentsIds = this.profiles.filter((profile) => profile.id !== 'ticket').map((profile) => profile.id);
      await this.escalationRepositoryV2.createTicketEscalation(data, reviewsData);

      this.ticket.escalation = {
        id: 0,
        ticket: {} as TicketInterface,
        escalatedFrom: data.escalatedFrom,
        shiftManager: data.shiftManager,
        departmentManager: data.departmentManager,
        priority: data.priority,
        reason: data.reason,
        status: data.status,
        timestamp: new Date().toLocaleString('sv-se'),
      };
      await this.addReviews(reviewsData);

      this.$emit('ticket-reviewed', this.ticket);
      this.$successDialog.show('Escalation created Successfully');
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private getDataForFeedbackDialog(): void {
    if (this.formData[this.selectedUser].reviewedId === 'ticket') {
      this.dataForFeedbackDialog = undefined;
      return;
    }
    const user = this.profiles.find((profile) => profile.id === this.formData[this.selectedUser].reviewedId);

    if (!user) {
      this.dataForFeedbackDialog = undefined;
      return;
    }
    const scores: string[] = [];
    Object.keys(this.formData[this.selectedUser].scores).forEach((scoreTypeId) => {
      const type = this.scoreTypes.find((st) => st.id === Number(scoreTypeId));
      const grade = this.scoreGrades
        .find((sg) => sg.id === Number(this.formData[this.selectedUser].scores[Number(scoreTypeId)]));

      if (!type || !grade) {
        return;
      }

      scores.push(`${type.label} - ${grade.label}`);
    });

    this.dataForFeedbackDialog = {
      user,
      scores,
      comments: this.formData[this.selectedUser].comments,
      tags: this.selectedTags,
      usersMap: this.commentProfiles,
    };
  }

  private assignCommentsProfiles(profiles: ProfileMapInterface): void {
    this.commentProfiles = profiles;
    this.getDataForFeedbackDialog();
  }

  private extractReviewData(): void {
    this.ticket.tags.forEach((ticketTag) => {
      const tag = this.tags.find((t) => t.id === ticketTag.tagId);
      if (!tag) {
        return;
      }

      this.formData.ticket.tags.push(tag);
    });

    this.ticket.reviews.forEach((review) => {
      this.formData[review.reviewedId].comments = review.comments;

      review.tags.forEach((reviewTag) => {
        const tag = this.tags.find((t) => t.id === reviewTag.tagId);
        if (!tag) {
          return;
        }

        this.formData[review.reviewedId].tags.push(tag);
      });

      review.scores.forEach((score) => {
        this.formData[review.reviewedId].scores[score.scoreTypeId] = score.scoreGradeId;
      });
    });

  }

  private addReviews(reviewsData: TicketReviewFormDataInterface[]): void {
    reviewsData.forEach((reviewData) => {
      if (reviewData.reviewedId === 'ticket' || !Object.values(reviewData.scores).length) {
        return;
      }

      this.ticket.reviews.push({
        id: 0,
        reviewedId: reviewData.reviewedId,
        reviewerId: reviewData.reviewerId,
        comments: reviewData.comments.map((c) => ({
          id: 0,
          author: c.author,
          text: c.text,
          rate: c.rate,
          timestamp: c.timestamp,
        })),
        isDraft: reviewData.isDraft,
        scores: Object.keys(reviewData.scores).map((scoreTypeId) => ({
          id: 0,
          scoreTypeId: Number(scoreTypeId),
          scoreGradeId: reviewData.scores[Number(scoreTypeId)],
        })),
        deletedAt: undefined,
        tags: reviewData.tags.map((tag) => ({ tagId: tag.id })),
        timestamp: new Date(),
      });
    });
  }

  private reviewHasUnsentComment(): boolean {
    return this.$refs['review-comments']['ticketComment'] && this.$refs['review-comments']['ticketComment'].trim() !== '';
  }
}
